import { useCallback, useContext, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { batch, useDispatch, useSelector } from 'react-redux';
import { cloneDeep, isEmpty, isEqual, isFunction, noop } from 'lodash';
import { FORM_MODES } from 'config/formModes';
import { useCurrentValueGetter, useMergeWithValueCallback, useNavigate } from 'hooks';
import { useDrawerStack } from 'hooks/queryParams/useDrawerStack';
import { applyRecommendationSuccess } from 'modules/campaigns/actions/applyRecommendationSuccess';
import formValidationErrorIndication from 'modules/campaigns/config/indications/validationIndication';
import { PATH_TO_ENTITY_MAP } from 'modules/campaigns/config/routes/entity';
import { useRecommendationService } from 'modules/campaigns/modules/performance-recommendations/hooks/useRecommendationService';
import { clearRecommendations } from 'modules/recommendations';
import { recommendationsDataSelector, recommendationsIdSelector } from 'modules/recommendations/selectors';
import { addIndication, INDICATION_TYPES } from 'modules/taboola-common-frontend-modules/Indications';
import { useFormDataContext, useFormWizardSteps } from 'modules/taboola-common-frontend-modules/formData';
import { useMultiLayerDrawerEnabled } from 'modules/taboola-common-frontend-modules/multi-layer-drawer/hooks/useMultiLayerDrawerEnabled';
import { useMultiLayerPathParams } from 'modules/taboola-common-frontend-modules/multi-layer-drawer/hooks/useMultiLayerPathParams';
import { getRrandFromData } from 'services/callAjax/responseParserUtils';
import { COMPONENT_STATUS } from 'services/constants';
import { removeIndication } from '../../Indications';
import { AccountContext } from '../../account-configurations';
import { APP_EVENT_TYPE, useAppEventContext } from '../../app-events-aggregator';
import { CHAT_AGENT_ACTION_STATUS } from '../../chat-agent/config/chatAgentActionStatus';
import { addAutoScrollCallback, allKeysList } from '../../formData/scroll-utils';
import { throwCriticalError } from '../../formData/utils/throwCriticalError';
import { gtmTracker } from '../../gtmTracker';
import { useUnsavedChangesController } from '../../navigation';
import { immutableMergeWith } from '../../utils/customMergers';
import { useValidationContext } from '../../validations/ValidationContext';
import { getInvalidFieldsWithMessages } from '../utils/getInvalidFieldsWithMessages';
import { hasFormMode } from '../utils/hasFormMode';

const emptyProps = {};
const noopPromise = () => Promise.resolve();
export const useFormState = ({
    formDataFetcher: formDataFetcherProp,
    fetchedFormData,
    recommendationData: recommendationDataProp,
    autoScroll = true,
    skipChatContextRegistration,
    waitAccountFetched: waitAccountFetchedProp = true,
    onCancel = noop,
    disableDataChangeDetection = false,
} = emptyProps) => {
    const {
        data,
        setData,
        patchExternalData,
        setFetchedData,
        mode,
        setSubmitStatus,
        setFetchStatus,
        fetchStatus,
        formDataFetcher = formDataFetcherProp,
        setExternalData,
        waitDelayedFields,
        path,
        setResponse,
        externalData,
        beforeUnmount,
        hasDataChanges: hasDataChangesFromContext,
        setIsInitialRecommendationsReady,
    } = useFormDataContext();

    // Allow disabling data change detection to prevent unsaved changes popup
    const hasDataChanges = !disableDataChangeDetection && hasDataChangesFromContext;
    const externalDataGetter = useCurrentValueGetter(externalData);
    const isMultiLayerDrawerEnabled = useMultiLayerDrawerEnabled();

    const navigate = useNavigate();
    const { campaignId, campaignsGroupId, entity } = useMultiLayerPathParams();
    const entityGetter = useCurrentValueGetter(entity);

    const { account } = useContext(AccountContext);
    const { isAccountFetched } = account;

    const hasAccountBeenFetchedOnce = useRef(false);
    //TODO: DEV-162332 - change this condition after DEV-159230 is resolved
    const waitAccountFetched = !!campaignsGroupId || waitAccountFetchedProp ? !!isAccountFetched : true;

    if (waitAccountFetched && !hasAccountBeenFetchedOnce.current) {
        hasAccountBeenFetchedOnce.current = true;
    }

    const { pop, stack } = useDrawerStack();

    const { validate: originValidate, optionalValidate, validationService } = useValidationContext();
    const validate = useCallback(async () => {
        await waitDelayedFields();
        return originValidate();
    }, [waitDelayedFields, originValidate]);

    const {
        isError: formInitError,
        isSuccess,
        isFetching: formInitFetching,
        data: formData,
    } = useQuery(
        ['init-form-data-fetcher', recommendationDataProp],
        async () => {
            const resolvedDataFetcher = formDataFetcher || noopPromise;
            const formData = await resolvedDataFetcher();

            if (formDataFetcher && !formData) {
                throw new Error('Error initializing form data');
            }

            return formData ?? {};
        },
        {
            refetchOnWindowFocus: false,
            refetchOnMount: 'always',
            enabled: hasAccountBeenFetchedOnce.current,
        }
    );
    const formInitSuccess = waitAccountFetched && isSuccess;
    const dispatch = useDispatch();
    const { applyRecommendation } = useRecommendationService();
    const recommendationDataFromCache = useSelector(recommendationsDataSelector);
    const recommendationId = useSelector(recommendationsIdSelector);
    const recommendationData = recommendationDataProp ?? recommendationDataFromCache;
    const customMergeWith = useMergeWithValueCallback();
    const { push: pushAppEvent } = useAppEventContext();

    const initFormData = useCallback(
        passedData => {
            batch(() => {
                const safeRecommendationData = cloneDeep(recommendationData);

                if (passedData || recommendationData) {
                    const mergedData = customMergeWith(cloneDeep(passedData || {}), safeRecommendationData);

                    setData(mergedData);
                    setFetchedData(passedData);
                }
                if (recommendationData) {
                    const entityData = recommendationData[entityGetter()];
                    const safeRecommendationData = cloneDeep(entityData || recommendationData);

                    setExternalData(safeRecommendationData);
                }
            });
        },
        [recommendationData, customMergeWith, setData, setFetchedData, entityGetter, setExternalData]
    );

    const dataGetter = useCurrentValueGetter(data);
    const onBeforeConfirmCancelNavigation = useCallback(async () => {
        onCancel();
        return await pushAppEvent({
            type: APP_EVENT_TYPE.FORM_ACTION,
            status: CHAT_AGENT_ACTION_STATUS.CANCEL,
            data: { [entityGetter()]: dataGetter() },
            id: PATH_TO_ENTITY_MAP[entityGetter()],
        });
    }, [dataGetter, entityGetter, onCancel, pushAppEvent]);

    useUnsavedChangesController({
        formPath: path,
        hasDataChanges,
        onBeforeConfirmCancelNavigation,
        mode,
    });

    const handleValidationError = useCallback(() => {
        dispatch(addIndication(formValidationErrorIndication));
    }, [dispatch]);

    const save = useCallback(
        async (saveFormData, successUrl, skipUnmount) => {
            let createdFormData;
            let rrand;
            try {
                createdFormData = await saveFormData(dataGetter());
                rrand = getRrandFromData(createdFormData);
                if (!createdFormData) {
                    throwCriticalError(
                        new Error("Wrong form submit flow, doesn't return data and doesn't throw error")
                    );
                }
                if (!rrand) {
                    gtmTracker.trackError('Empty rrand in form submit');
                }
            } catch (error) {
                rrand = error?.rrand;
            }

            if (!createdFormData) {
                setSubmitStatus(COMPONENT_STATUS.ERROR);
                await pushAppEvent({
                    type: APP_EVENT_TYPE.FORM_ACTION,
                    data: { [entityGetter()]: dataGetter() },
                    status: CHAT_AGENT_ACTION_STATUS.FAILURE,
                    rrand,
                    id: PATH_TO_ENTITY_MAP[entityGetter()],
                });
                return;
            }

            if (!isEmpty(createdFormData)) {
                await pushAppEvent({
                    type: APP_EVENT_TYPE.FORM_ACTION,
                    status: CHAT_AGENT_ACTION_STATUS.SUCCESS,
                    data: { [entityGetter()]: createdFormData },
                    rrand,
                    id: PATH_TO_ENTITY_MAP[entityGetter()],
                });
            }

            setSubmitStatus(COMPONENT_STATUS.ACTIVE);
            if (!skipUnmount) {
                beforeUnmount();
            }

            dispatch(removeIndication({ type: INDICATION_TYPES.ERROR }));
            if (recommendationId) {
                await applyRecommendation(recommendationId);

                if (campaignId) {
                    dispatch(applyRecommendationSuccess({ id: campaignId }));
                }
            }

            return createdFormData;
        },
        [
            setSubmitStatus,
            dispatch,
            beforeUnmount,
            recommendationId,
            dataGetter,
            pushAppEvent,
            entityGetter,
            applyRecommendation,
            campaignId,
        ]
    );

    const submit = useCallback(
        async ({ saveFormData, successUrl, skipValidation, skipUnmount, shouldPopLastLayer = true }) => {
            if (mode === FORM_MODES.PREVIEW) {
                return;
            }

            setSubmitStatus(COMPONENT_STATUS.LOADING);
            const isFormValid = skipValidation ?? (await validate());
            if (!isFormValid) {
                setSubmitStatus(COMPONENT_STATUS.INITIAL);
                handleValidationError();
                pushAppEvent({
                    type: APP_EVENT_TYPE.FORM_ACTION,
                    status: CHAT_AGENT_ACTION_STATUS.FAILURE,
                    data: getInvalidFieldsWithMessages(validationService.getInvalidFields(), dataGetter()),
                    id: PATH_TO_ENTITY_MAP[entityGetter()],
                });
                return;
            }
            const saveCallback = () => save(saveFormData, successUrl, skipUnmount);
            const cancelCallback = () => setSubmitStatus(COMPONENT_STATUS.INITIAL);

            const createdFormData = await optionalValidate(saveCallback, cancelCallback);
            if (createdFormData) {
                setResponse(createdFormData);

                const navigatePath = isFunction(successUrl) ? await successUrl(createdFormData) : successUrl;

                const isFormMode = hasFormMode(navigatePath);

                if (shouldPopLastLayer && isMultiLayerDrawerEnabled) {
                    pop({ [entityGetter()]: createdFormData }, isFormMode && navigatePath);
                }

                if (navigatePath && stack.length <= 1 && (!isMultiLayerDrawerEnabled || !isFormMode)) {
                    navigate(navigatePath);
                }

                return createdFormData;
            }
        },
        [
            handleValidationError,
            validate,
            optionalValidate,
            save,
            mode,
            setSubmitStatus,
            pushAppEvent,
            dataGetter,
            validationService,
            setResponse,
            pop,
            navigate,
            entityGetter,
            isMultiLayerDrawerEnabled,
            stack,
        ]
    );

    const submitStep = useCallback(
        ({ saveFormData, successUrl, skipValidation }) => {
            return submit({
                saveFormData,
                successUrl,
                skipValidation,
                skipUnmount: true,
                shouldPopLastLayer: false,
            });
        },
        [submit]
    );

    const { step, setStep, prevStep, nextStep, registerStep, onStepGetActive } = useFormWizardSteps({
        validate,
        optionalValidate,
        handleValidationError,
        initialStep: recommendationData?.initialStep,
        skipChatContextRegistration,
    });

    // This effect handles formDataFetcher and fetchedFormData statuses
    useEffect(() => {
        if (fetchStatus === COMPONENT_STATUS.ACTIVE) {
            return;
        }

        const { isSuccess, isError, data } = fetchedFormData ?? { isSuccess: true, data: {} };

        if (formInitFetching && fetchStatus !== COMPONENT_STATUS.LOADING) {
            setFetchStatus(COMPONENT_STATUS.LOADING);
            return;
        }

        if (formInitFetching) {
            return;
        }

        if ((formInitError || isError) && fetchStatus !== COMPONENT_STATUS.ERROR) {
            setFetchStatus(COMPONENT_STATUS.ERROR);
            return;
        }
        if (formInitSuccess && isSuccess && fetchStatus !== COMPONENT_STATUS.ACTIVE) {
            const resolvedFormData = immutableMergeWith(formData, data);
            initFormData(resolvedFormData);
            setFetchStatus(COMPONENT_STATUS.ACTIVE);
        }
    }, [
        fetchedFormData,
        formData,
        setFetchStatus,
        fetchStatus,
        initFormData,
        formInitError,
        formInitSuccess,
        formInitFetching,
    ]);

    // Scrolling to first recommended data field
    useEffect(() => {
        if (isEmpty(recommendationData) || fetchStatus !== COMPONENT_STATUS.ACTIVE || !autoScroll) {
            return;
        }

        const entityData = recommendationData[entityGetter()];
        const fieldNames = allKeysList(entityData || recommendationData);
        const callback = () => validationService.manuallyTriggerValidationFocus(fieldNames);
        addAutoScrollCallback(callback);
    }, [recommendationData, fetchStatus, validationService, autoScroll, entityGetter]);

    //handle recommendation data change and merge it with form data
    useEffect(() => {
        if (isEmpty(recommendationData)) {
            setIsInitialRecommendationsReady(true);
        }

        if (
            isEmpty(recommendationData) ||
            fetchStatus !== COMPONENT_STATUS.ACTIVE ||
            isEqual(recommendationData, externalDataGetter())
        ) {
            return;
        }

        const entityData = recommendationData[entityGetter()];
        const safeRecommendationData = cloneDeep(entityData || recommendationData);
        patchExternalData({ data: safeRecommendationData, merger: customMergeWith });

        setIsInitialRecommendationsReady(true);
    }, [
        customMergeWith,
        recommendationData,
        patchExternalData,
        fetchStatus,
        externalDataGetter,
        entityGetter,
        setIsInitialRecommendationsReady,
    ]);

    // Clearing recommendations and changes tracker
    useEffect(
        () => () => {
            if (autoScroll && mode !== FORM_MODES.PREVIEW) {
                dispatch(clearRecommendations());
            }
        },
        [dispatch, autoScroll, mode]
    );

    useEffect(() => {
        pushAppEvent({
            type: APP_EVENT_TYPE.FORM_ACTION,
            status: CHAT_AGENT_ACTION_STATUS.IN_PROGRESS,
            id: PATH_TO_ENTITY_MAP[entityGetter()],
        });
    }, [pushAppEvent, entityGetter]);

    return {
        submit,
        submitStep,
        step,
        setStep,
        prevStep,
        nextStep,
        registerStep,
        onStepGetActive,
    };
};
